import { useRef, useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import EmailEditor, { EditorRef } from "react-email-editor";
import SafeArea from "components/safe-area";
import FlatContainer from "components/container/flat";
import { Button, OutlineButton } from "components/button";
import { Input } from "components/input";
import { useModalContext } from "components/modal";
import { IMergeField } from "views/outreach/templates/types/IEmailTemplate";
import { useCampaignEditor } from "./hooks/useCampaignEditor";
import FilterRecipientsModal from "../components/FilterRecipientsModal";
import SendConfirmModal from "../components/SendConfirmModal";
import ScheduleModal from "../components/ScheduleModal";
import ConfirmActionModal from "../components/ConfirmActionModal";

const CampaignEditor = () => {
  const { t } = useTranslation();
  const tBase = "views.outreach.campaigns.editor";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const history = useHistory();
  const emailEditorRef = useRef<EditorRef>(null);

  const {
    name,
    setName,
    subject,
    setSubject,
    campaignType,
    setCampaignType,
    messageText,
    setMessageText,
    templateId,
    filters,
    selectedCustomers,
    isEditing,
    campaign,
    templates,
    mergeFields,
    mergeTags,
    recipientCount,
    isCountLoading,
    setEditorReady,
    showFilterModal,
    setShowFilterModal,
    showSendConfirm,
    setShowSendConfirm,
    showScheduleModal,
    setShowScheduleModal,
    isScheduling,
    showSaveFirstModal,
    setShowSaveFirstModal,
    isLoading,
    isSaving,
    errors,
    touched,
    setTouched,
    isDirty,
    isSending,
    handleSave,
    handleSendNow,
    handleScheduleClick,
    confirmSend,
    confirmSchedule,
    handleTemplateChange,
    handleApplyFilters,
    handleFilterRecipientsClick,
    handleSaveFirstConfirm,
  } = useCampaignEditor(emailEditorRef);

  const { setModal } = useModalContext();
  const isDirtyRef = useRef(false);
  isDirtyRef.current = isDirty;

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isDirtyRef.current) {
        e.preventDefault();
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  useEffect(() => {
    const unblock = history.block((location) => {
      if (!isDirtyRef.current) return undefined;
      const targetPath = location.pathname + (location.search || "");
      setModal({
        label: tr("unsavedChangesTitle"),
        component: (
          <ConfirmActionModal
            title=""
            message={tr("unsavedChangesMessage")}
            confirmLabel={tr("leaveButton")}
            cancelLabel={tr("stayButton")}
            variant="danger"
            onConfirm={() => {
              isDirtyRef.current = false;
              unblock();
              history.push(targetPath);
            }}
          />
        ),
      });
      return false;
    });
    return () => unblock();
  }, [history, setModal, tr]);

  const smsTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);

  const GSM7_SEGMENT_SIZE = 160;

  const smsSegmentCount = Math.max(1, Math.ceil(messageText.length / GSM7_SEGMENT_SIZE));

  const insertMergeField = useCallback(
    (fieldKey: string) => {
      const tag = `{{${fieldKey}}}`;
      const textarea = smsTextareaRef.current;
      if (textarea) {
        const start = textarea.selectionStart;
        const end = textarea.selectionEnd;
        const newText =
          messageText.substring(0, start) + tag + messageText.substring(end);
        setMessageText(newText);
        // Restore cursor position after the inserted tag
        requestAnimationFrame(() => {
          textarea.focus();
          const cursorPos = start + tag.length;
          textarea.setSelectionRange(cursorPos, cursorPos);
        });
      } else {
        setMessageText(messageText + tag);
      }
    },
    [messageText, setMessageText]
  );

  const onEditorReady = () => {
    setEditorReady(true);
    if (isEditing && campaign?.designJson) {
      emailEditorRef.current?.editor?.loadDesign(
        campaign.designJson as Parameters<
          NonNullable<typeof emailEditorRef.current>["editor"]["loadDesign"]
        >[0]
      );
    }
  };

  if (isLoading) {
    return (
      <SafeArea>
        <div className="py-12 flex items-center justify-center">
          <p className="text-gray-600 text-lg">{tr("loading")}</p>
        </div>
      </SafeArea>
    );
  }

  return (
    <SafeArea>
      {/* Header */}
      <FlatContainer
        title={
          <h2>{isEditing ? tr("editTitle") : tr("newTitle")}</h2>
        }
        buttons={[
          <OutlineButton
            key="back"
            size="small"
            onClick={() => history.push("/outreach/campaigns")}
          >
            {tr("backToList")}
          </OutlineButton>,
        ]}
      />

      {/* Campaign Type Toggle */}
      <div className="py-4 border-b">
        {!isEditing ? (
          <div className="flex items-center gap-2 mb-4">
            <label className="text-sm font-semibold mr-2">Type:</label>
            <button
              type="button"
              className={`px-4 py-1.5 rounded-l text-sm font-medium border transition-colors ${
                campaignType === "email"
                  ? "bg-[#ff4f00] text-white border-[#ff4f00]"
                  : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
              }`}
              onClick={() => setCampaignType("email")}
            >
              Email
            </button>
            <button
              type="button"
              className={`px-4 py-1.5 rounded-r text-sm font-medium border transition-colors ${
                campaignType === "sms"
                  ? "bg-[#ff4f00] text-white border-[#ff4f00]"
                  : "bg-white text-gray-600 border-gray-300 hover:bg-gray-50"
              }`}
              onClick={() => setCampaignType("sms")}
            >
              SMS
            </button>
          </div>
        ) : (
          <div className="flex items-center gap-2 mb-4">
            <label className="text-sm font-semibold mr-2">Type:</label>
            <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 uppercase">
              {campaignType}
            </span>
          </div>
        )}

        {/* Form fields */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Name */}
          <Input
            label={tr("nameLabel")}
            value={name}
            onChange={(e: any) => setName(e.target.value)}
            onBlur={() => setTouched((prev) => ({ ...prev, name: true }))}
            placeholder={tr("namePlaceholder")}
            error={errors.name && touched.name ? errors.name : ""}
          />

          {/* Subject - email only */}
          {campaignType === "email" && (
            <Input
              label={tr("subjectLabel")}
              value={subject}
              onChange={(e: any) => setSubject(e.target.value)}
              onBlur={() =>
                setTouched((prev) => ({ ...prev, subject: true }))
              }
              placeholder={tr("subjectPlaceholder")}
              error={errors.subject && touched.subject ? errors.subject : ""}
            />
          )}

          {/* Template selector */}
          <div>
            <label className="block text-sm font-semibold mb-1">
              {tr("templateLabel")}
            </label>
            <select
              className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:border-[#ff4f00]"
              value={templateId || ""}
              onChange={(e) =>
                handleTemplateChange(e.target.value || null)
              }
            >
              <option value="">{tr("noTemplate")}</option>
              {templates.map((tpl) => (
                <option key={tpl.id} value={tpl.id}>
                  {tpl.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Filter and Recipient Count */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t">
          <div className="flex items-center gap-4">
            <OutlineButton
              size="small"
              onClick={handleFilterRecipientsClick}
            >
              {tr("filterRecipients")}
            </OutlineButton>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-500">
                {tr("recipients")}:
              </span>
              {isCountLoading ? (
                <span className="text-sm text-gray-400">...</span>
              ) : (
                <span className="text-lg font-bold text-[#ff4f00]">
                  {recipientCount ?? "--"}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* SMS Message Composer */}
      {campaignType === "sms" && (
        <div className="mt-4">
          <div className="border rounded p-4">
            <label className="block text-sm font-semibold mb-2">
              Message Text
            </label>

            {/* Merge field insert buttons */}
            {mergeFields.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-3">
                <span className="text-xs text-gray-500 self-center mr-1">
                  Insert:
                </span>
                {mergeFields.map((field: IMergeField) => (
                  <button
                    key={field.key}
                    type="button"
                    className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200 hover:bg-[#ff4f00] hover:text-white hover:border-[#ff4f00] transition-colors cursor-pointer"
                    onClick={() => insertMergeField(field.key)}
                    title={field.description || field.label}
                  >
                    {field.label}
                  </button>
                ))}
              </div>
            )}

            <textarea
              ref={smsTextareaRef}
              className="w-full p-3 border border-gray-300 rounded focus:outline-none focus:border-[#ff4f00] resize-y font-mono text-sm"
              rows={6}
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              onBlur={() =>
                setTouched((prev) => ({ ...prev, messageText: true }))
              }
              placeholder="Type your SMS message here..."
            />

            {/* Character count and segment indicator */}
            <div className="flex items-center justify-between mt-2">
              <div className="flex items-center gap-4">
                <span className="text-xs text-gray-500">
                  {messageText.length} / {GSM7_SEGMENT_SIZE} characters
                </span>
                <span className="text-xs text-gray-500">
                  {smsSegmentCount} {smsSegmentCount === 1 ? "segment" : "segments"}
                  {smsSegmentCount > 1 && (
                    <span className="text-gray-400 ml-1">
                      ({GSM7_SEGMENT_SIZE} chars per segment)
                    </span>
                  )}
                </span>
              </div>
              {errors.messageText && touched.messageText && (
                <span className="text-xs text-red-500">{errors.messageText}</span>
              )}
            </div>

            <p className="mt-3 text-xs text-gray-400 italic">
              &quot;Reply STOP to unsubscribe&quot; will be automatically appended
            </p>
          </div>
        </div>
      )}

      {/* Email Editor */}
      {campaignType === "email" && (
        <div className="mt-4">
          <div className="flex items-center justify-end mb-2">
            <OutlineButton
              size="small"
              onClick={() => {
                const editor = emailEditorRef.current?.editor;
                if (!editor) return;
                if (isPreviewing) {
                  editor.hidePreview();
                  setIsPreviewing(false);
                } else {
                  editor.showPreview("desktop");
                  setIsPreviewing(true);
                }
              }}
            >
              {isPreviewing ? tr("backToEditor") : tr("preview")}
            </OutlineButton>
          </div>
          <div
            className="border rounded overflow-hidden"
            style={{ height: 600 }}
          >
            <EmailEditor
              ref={emailEditorRef}
              onReady={onEditorReady}
              minHeight="600px"
              options={{
                mergeTags,
                features: { textEditor: { tables: true } },
              }}
            />
          </div>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex gap-3 py-4">
        <OutlineButton
          onClick={() => history.push("/outreach/campaigns")}
          disabled={isSaving}
        >
          {tr("cancel")}
        </OutlineButton>
        <OutlineButton
          onClick={handleSave}
          disabled={isSaving}
          isLoading={isSaving}
        >
          {isSaving ? tr("saving") : tr("saveDraft")}
        </OutlineButton>
        <div className="relative group">
          <OutlineButton
            onClick={handleScheduleClick}
            disabled={!isEditing || isSaving || isSending || isScheduling}
          >
            {tr("schedule")}
          </OutlineButton>
          {!isEditing && (
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              {tr("saveFirst")}
            </div>
          )}
        </div>
        <div className="relative group">
          <Button
            primary
            onClick={handleSendNow}
            disabled={!isEditing || isSaving || isSending}
          >
            {tr("sendNow")}
          </Button>
          {!isEditing && (
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-1.5 bg-gray-800 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
              {tr("saveFirst")}
            </div>
          )}
        </div>
      </div>

      {/* Filter Modal */}
      <FilterRecipientsModal
        isOpen={showFilterModal}
        filters={filters}
        selectedCustomers={selectedCustomers}
        campaignType={campaignType}
        onApply={handleApplyFilters}
        onCancel={() => setShowFilterModal(false)}
      />

      {/* Send Confirmation Modal */}
      <SendConfirmModal
        isOpen={showSendConfirm}
        campaignName={name}
        subject={subject}
        campaignType={campaignType}
        messageText={messageText}
        recipientCount={recipientCount}
        isSending={isSending}
        onConfirm={confirmSend}
        onCancel={() => setShowSendConfirm(false)}
      />

      {/* Schedule Modal */}
      <ScheduleModal
        isOpen={showScheduleModal}
        campaignName={name}
        recipientCount={recipientCount}
        isScheduling={isScheduling}
        onConfirm={confirmSchedule}
        onCancel={() => setShowScheduleModal(false)}
      />

      {/* Save First Modal */}
      {showSaveFirstModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4 shadow-xl">
            <p className="text-gray-700 text-sm mb-6">
              {tr("saveFirst")}
            </p>
            <div className="flex justify-end gap-3">
              <OutlineButton
                size="small"
                onClick={() => setShowSaveFirstModal(false)}
              >
                {tr("cancel")}
              </OutlineButton>
              <Button
                primary
                size="small"
                onClick={handleSaveFirstConfirm}
                disabled={isSaving}
              >
                {isSaving ? tr("saving") : tr("saveDraft")}
              </Button>
            </div>
          </div>
        </div>
      )}
    </SafeArea>
  );
};

export default CampaignEditor;
